import { AnthropicProviderOptions, createAnthropic } from '@ai-sdk/anthropic';
import { deepseek, DeepSeekLanguageModelOptions } from '@ai-sdk/deepseek';
import { createGoogleGenerativeAI, GoogleGenerativeAIProviderSettings } from '@ai-sdk/google';
import { createOpenAI, OpenAIChatLanguageModelOptions } from '@ai-sdk/openai';
import type { LanguageModelV3, LanguageModelV3CallOptions } from '@ai-sdk/provider';
import { xai, XaiProviderSettings } from '@ai-sdk/xai';
import { wrapLanguageModel } from 'ai';

import { falPriceUSD } from './falPricing';

export { falPriceUSD, type FalPriceOptions } from './falPricing';

export type ModelType = 'chat' | 'tts' | 'image_generation' | 'video_generation';

export type ProviderId =
  | 'openai'
  | 'anthropic'
  | 'google'
  | 'xai'
  | 'deepseek'
  | 'perplexity'
  | 'elevenlabs'
  | 'fal';

export type ModelId =
  // Poppy's own picks: branded chat models that run on POPPY_BACKING_MODEL_ID and
  // POPPY_ULTRA_BACKING_MODEL_ID.
  | 'poppy'
  | 'poppy-ultra'
  | 'gpt-4o'
  | 'gpt-4o-mini'
  | 'o3-mini'
  | 'gpt-4.1'
  | 'claude-4-sonnet-20250514'
  | 'claude-sonnet-4-5'
  | 'claude-sonnet-4-6'
  | 'claude-sonnet-5'
  | 'claude-sonnet-5-5'
  | 'claude-haiku-4-5'
  | 'claude-haiku-5-5'
  | 'claude-opus-4-6'
  | 'claude-opus-4-7'
  | 'claude-opus-4-8'
  | 'claude-opus-5'
  | 'claude-opus-5-5'
  | 'grok-4.5'
  | 'grok-4.7'
  | 'gemini-2.5-pro'
  | 'gemini-3.8-flash'
  | 'deepseek-flash'
  | 'deepseek-v4-pro'
  | 'gpt-5'
  | 'gpt-5.6-terra'
  | 'gpt-6.1-terra'
  | 'gpt-5.4-mini'
  | 'gpt-5.5'
  | 'deepseek-flash'
  | 'elevenlabs'
  | 'gpt-image-2'
  | 'gpt-image-2.5-sunburst'
  | 'nanobanana-pro'
  | 'nanobanana-2'
  | 'fal-ai/veo3.1'
  | 'fal-ai/kling-video/v2.5-turbo/pro'
  | 'fal-ai/kling-video/v3/pro'
  | 'bytedance/seedance-2.0'
  | 'google/gemini-omni-flash/v1.1'
  | 'minimax/h3'
  | 'alibaba/wan-3.0'
  | 'fal-ai/bytedance/seedream/v4.5'
  | 'perplexity/sonar-pro';

/**
 * Branded models are ids users pick that run on another registered model: Poppy and Poppy Ultra.
 * They sit in the chat picker like any other model, and the model behind them can change without
 * anyone noticing. Everything about a branded model (pricing, token limit, provider, fallback) is
 * taken from its backing model; only the id, the names and, for Poppy Ultra, how hard it thinks
 * differ.
 */
export type BrandedModelId = 'poppy' | 'poppy-ultra';
/** A model id that names a real provider model (everything but the branded ids). */
export type RegisteredModelId = Exclude<ModelId, BrandedModelId>;

// Default model ID used across all applications
export const DEFAULT_MODEL_ID: ModelId = 'claude-sonnet-5-5';
/** OpenAI chat choice. Roll back by changing only this value to 'gpt-5.6-terra'. */
export const OPENAI_CHAT_MODEL_ID: 'gpt-6.1-terra' | 'gpt-5.6-terra' = 'gpt-6.1-terra';
/**
 * Models taken out of use, and the model a request naming one runs on instead (the API, a
 * chatbot or an old chat still asking for it). Sonnet 5 → Sonnet 5.5 on 2026-10-31: the same
 * price per token, a newer model. The Copilot and the image editor still name Sonnet 5 directly
 * while they are benchmarked against Poppy. GPT-5.5 / GPT-5.6 Terra chat requests move to
 * GPT-6.1 Terra; internal structured-output workers can still name the old models directly.
 */
export const RETIRED_MODEL_SUCCESSORS: Partial<Record<ModelId, ModelId>> = {
  'claude-sonnet-5': 'claude-sonnet-5-5',
  'gpt-5.5': OPENAI_CHAT_MODEL_ID,
  'gpt-5.6-terra': OPENAI_CHAT_MODEL_ID,
  // Keep already-saved 6.1 chats working when the one-line rollback is used.
  'gpt-6.1-terra': OPENAI_CHAT_MODEL_ID,
};
/** The model a request for `modelId` runs on: its successor if it was retired, else itself. */
export const currentModelId = (modelId: ModelId): ModelId =>
  RETIRED_MODEL_SUCCESSORS[modelId] ?? modelId;
/**
 * ⚠️ THE ONE PLACE TO SWAP THE MODEL BEHIND "POPPY".
 * Change this id and every Poppy chat runs on the new model, billed at its price, with its
 * token limit, provider options and fallback. Nothing else needs to change. Must be a chat
 * model, and the deployment needs that provider's API key (see hasProviderKey).
 * Today: DeepSeek V4.1 Flash, 92-96% cheaper than Sonnet 5 on the 2026-10-25 benchmark (#2737).
 */
export const POPPY_BACKING_MODEL_ID: RegisteredModelId = 'deepseek-flash';
/**
 * ⚠️ THE ONE PLACE TO SWAP THE MODEL BEHIND "POPPY ULTRA", the model that thinks before it
 * answers. Same contract as POPPY_BACKING_MODEL_ID; Poppy Ultra always runs with thinking on
 * (POPPY_ULTRA_THINKING below), whichever DeepSeek model backs it.
 * Today: DeepSeek V4.1 Flash with thinking on. On the 2026-11-03 chat bench it passed 28/28
 * against V4 Pro's 26/28, at 2.5x less per turn, faster, and it sees images (Pro is text-only).
 */
export const POPPY_ULTRA_BACKING_MODEL_ID: RegisteredModelId = 'deepseek-flash';
// The model a brand-new chat starts on. Only the chat surfaces read this; everything
// else that means "the default model" (Copilot, artifact workers, tours, the API app)
// keeps DEFAULT_MODEL_ID.
export const DEFAULT_CHAT_MODEL_ID: ModelId = 'poppy';
/**
 * ⚠️ THE ONE LIST OF CHAT MODELS EVERY PLAN CAN PICK: Poppy and Poppy Ultra. Every other chat
 * model (Claude, GPT, Gemini, Grok, ...) is for Creator plans and up (@poppy/utils
 * hasPremiumModelAccess), and everyone else's turns on one run on PREMIUM_MODEL_FALLBACK_ID.
 * Image and video models are tools, not chat picks: every plan keeps them. Basic and Starter were
 * 73% of Sonnet 5.5 chat spend in Nov 2026, at about 15x Poppy's cost per message.
 */
export const EVERY_PLAN_CHAT_MODEL_IDS: readonly ModelId[] = ['poppy', 'poppy-ultra'];
/** Whether `id` is a chat model only Creator plans and up can pick (every chat model but Poppy's). */
export const isPremiumChatModel = (id: string): boolean => {
  const model = MODELS[id as ModelId];
  return (
    !!model && isChatModel(model) && !(EVERY_PLAN_CHAT_MODEL_IDS as readonly string[]).includes(id)
  );
};
/** What a premium model turns into for a plan without it: Poppy, the model the upgrade card offers. */
export const PREMIUM_MODEL_FALLBACK_ID: ModelId = DEFAULT_CHAT_MODEL_ID;
// Default image generation model (used by image tools and image-mode UI)
export const DEFAULT_IMAGE_MODEL_ID: ModelId = 'gpt-image-2';
// Default video generation model (used by video tools)
export const DEFAULT_VIDEO_MODEL_ID: ModelId = 'google/gemini-omni-flash/v1.1';

// Base pricing interfaces
interface ChatPricing {
  inputPerTokenUSD: number;
  outputPerTokenUSD: number;
  // Anthropic cache pricing (for prompt caching)
  cacheWritePerTokenUSD?: number;
  cacheReadPerTokenUSD?: number;
  // Tiered pricing (e.g., Anthropic >200K, OpenAI GPT-5.6 Terra >272K)
  tieredPricingThreshold?: number;
  inputPerTokenUSD1M?: number;
  outputPerTokenUSD1M?: number;
  cacheWritePerTokenUSD1M?: number;
  cacheReadPerTokenUSD1M?: number;
  // Optional per-request cost
  costPerRequestUSD?: number;
}

interface TTSPricing {
  costPerCharacterUSD: number;
}

// Base metadata (common to all models)
interface BaseModelMetadata {
  id: ModelId;
  displayName: string;
  shortName: string;
  providerId: ProviderId;
  modelType: ModelType;
  // Optional UI tag used for dropdown badges
  uiTag?: 'cheap' | 'best-price' | 'best-image' | 'best-video' | 'new';
  // Gated behind 'experimental_models' PostHog feature flag
  experimental?: boolean;
}

// Chat model metadata
export interface ChatModelMetadata extends BaseModelMetadata {
  modelType: 'chat';
  tokenLimit: number;
  pricing: ChatPricing;
  /**
   * Set on a branded model only: the registered model its requests actually run on. Its
   * provider sees the backing id; the app (billing, ledger, analytics) sees the branded id.
   */
  backingModelId?: RegisteredModelId;
  /**
   * Set on a branded model only: who the model says it is, in place of the chat's plain
   * "You are <name>." It keeps the backing model from naming itself.
   */
  identityPrompt?: string;
  providerOptions?: {
    anthropic?: AnthropicProviderOptions;
    google?: GoogleGenerativeAIProviderSettings;
    xai?: XaiProviderSettings;
    openai?: OpenAIChatLanguageModelOptions;
    deepseek?: DeepSeekLanguageModelOptions;
  };
}

// TTS model metadata
export interface TTSModelMetadata extends BaseModelMetadata {
  modelType: 'tts';
  pricing: TTSPricing;
}

// Image generation model metadata. Image and video models are priced per fal endpoint
// (falPricing.ts).
export interface ImageGenModelMetadata extends BaseModelMetadata {
  modelType: 'image_generation';
}

export type VideoAspectRatio = 'auto' | '21:9' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16';

interface VideoGenModelOptions {
  defaultAspectRatio?: VideoAspectRatio;
  supportedAspectRatios?: VideoAspectRatio[];
  defaultDurationSeconds?: number;
  supportedDurationsSeconds?: number[];
  supportsAudio?: boolean;
  // Makes sound with every video, at one price: there is no toggle to send (supportsAudio false).
  alwaysMakesSound?: boolean;
  supportsResolution?: boolean;
  // 'number' sends the seconds as a JSON number.
  durationFormat?: 'suffix-s' | 'plain-seconds' | 'number';
  defaultResolution?: '720p' | '1080p';
  defaultNegativePrompt?: string;
  defaultCfgScale?: number;
  supportsAutoFix?: boolean;
  // Each fal endpoint's own input names, where they differ from the usual ones (check its schema).
  // The reference image's field; image_url when unset.
  imageField?: 'image_url' | 'start_image_url';
  // The image-to-video endpoint's last-frame field (sent only with a start frame); none when unset.
  endImageField?: 'end_image_url' | 'tail_image_url';
  // The sound toggle's field when supportsAudio; generate_audio when unset.
  audioField?: 'generate_audio' | 'audio';
  // Sent as resolution on every request, whatever was asked, in the endpoint's own spelling.
  fixedResolution?: string;
  // The image-to-video endpoint has no aspect_ratio: the video takes the image's shape.
  imageSetsAspectRatio?: boolean;
  // How to write this model's prompt, for the chat model that writes it (videoModelSummary).
  promptTips?: string;
}

// Video generation model metadata
export interface VideoGenModelMetadata extends BaseModelMetadata {
  modelType: 'video_generation';
  endpoints: {
    textToVideo: string;
    imageToVideo: string;
  };
  videoOptions?: VideoGenModelOptions;
}

// Union type for all model metadata
export type ModelMetadata =
  | ChatModelMetadata
  | TTSModelMetadata
  | ImageGenModelMetadata
  | VideoGenModelMetadata;

// Type guards
export function isChatModel(model: ModelMetadata): model is ChatModelMetadata {
  return model.modelType === 'chat';
}

export function isTTSModel(model: ModelMetadata): model is TTSModelMetadata {
  return model.modelType === 'tts';
}

export function isImageGenModel(model: ModelMetadata): model is ImageGenModelMetadata {
  return model.modelType === 'image_generation';
}

export function isVideoGenModel(model: ModelMetadata): model is VideoGenModelMetadata {
  return model.modelType === 'video_generation';
}

export function isExperimentalModel(modelId: ModelId): boolean {
  const model = MODELS[modelId];
  return model?.experimental === true;
}

/** Whether `modelId` is a branded model (Poppy) rather than a provider's own model. */
export function isBrandedModel(modelId: ModelId): modelId is BrandedModelId {
  const model = MODELS[modelId];
  return !!model && isChatModel(model) && !!model.backingModelId;
}

/**
 * The registered model a request for `modelId` runs on: the backing model for a branded id,
 * the id itself otherwise. Use it wherever behaviour depends on the real model (reasoning
 * options, temperature support, fallback choice); keep the branded id for anything the user
 * or the books see.
 */
export function resolveBackingModelId(modelId: ModelId): RegisteredModelId {
  const model = MODELS[modelId];
  if (model && isChatModel(model) && model.backingModelId) return model.backingModelId;
  return modelId as RegisteredModelId;
}

/** The env var that holds Poppy's own key for each provider that needs one here. */
const PROVIDER_API_KEY_ENV: Partial<Record<ProviderId, string>> = {
  openai: 'OPENAI_API_KEY',
  anthropic: 'ANTHROPIC_API_KEY',
  google: 'GOOGLE_GENERATIVE_AI_API_KEY',
  xai: 'XAI_API_KEY',
  deepseek: 'DEEPSEEK_API_KEY',
};

/**
 * Whether this process holds Poppy's own key for the provider `modelId` runs on (a branded id
 * checks its backing model). A deployment without that key (the API app has no
 * DEEPSEEK_API_KEY) can pick another model up front instead of failing the request. Providers
 * with no key mapping here are assumed configured.
 */
export function hasProviderKey(
  modelId: ModelId,
  env: Record<string, string | undefined> = process.env
): boolean {
  const providerId = MODELS[resolveBackingModelId(modelId)]?.providerId;
  const envName = providerId && PROVIDER_API_KEY_ENV[providerId];
  return !envName || !!env[envName];
}

// Models that reject a non-default `temperature` (the request 400s if it's sent):
// Anthropic reasoning models (Opus 4.7/4.8, Sonnet 5) removed sampling params, and
// the OpenAI GPT-5 / GPT-5.4 family use max_completion_tokens and reject temperature.
// Single source of truth — any code that sets `temperature` on a model call MUST
// gate it behind `supportsTemperature` so a default-model change can't 400 requests.
export const NO_TEMPERATURE_MODELS: ModelId[] = [
  'claude-opus-4-7',
  'claude-opus-4-8',
  'claude-opus-5',
  'claude-opus-5-5',
  'claude-sonnet-5',
  'claude-sonnet-5-5',
  'gpt-5',
  'gpt-5.6-terra',
  'gpt-5.4-mini',
  'gpt-5.5',
  'gpt-6.1-terra',
  // Accepts temperature, but Google says Gemini 3 loops or degrades below the default 1.0.
  'gemini-3.8-flash',
];

export function supportsTemperature(modelId: ModelId): boolean {
  return !NO_TEMPERATURE_MODELS.includes(resolveBackingModelId(modelId));
}

// Chat models that read a PDF natively (page images plus text) through an AI SDK file part.
// Others get the PDF's extracted text instead.
const NO_PDF_INPUT_MODELS: ModelId[] = ['o3-mini'];

export function supportsPdfInput(modelId: ModelId): boolean {
  const model = MODELS[modelId];
  if (!model || model.modelType !== 'chat') return false;
  if (NO_PDF_INPUT_MODELS.includes(resolveBackingModelId(modelId))) return false;
  return ['anthropic', 'openai', 'google'].includes(model.providerId);
}

// Text-only chat models: an image part makes the provider reject the whole turn.
const NO_IMAGE_INPUT_MODELS: ModelId[] = ['o3-mini', 'deepseek-v4-pro'];

/** Whether `modelId` can see images; a branded id answers for the model behind it. */
export function supportsImageInput(modelId: ModelId): boolean {
  return !NO_IMAGE_INPUT_MODELS.includes(resolveBackingModelId(modelId));
}

/**
 * The image models a tool caller (the Copilot, an assistant over MCP) may name, by the names
 * people use for them, the default first. Experimental models stay behind their flag.
 */
export const IMAGE_MODEL_CHOICES = {
  'gpt-image-2.5-flare': 'gpt-image-2',
  'gpt-image-2.5-sunburst': 'gpt-image-2.5-sunburst',
  'nano-banana-2.1': 'nanobanana-2',
  'nano-banana-pro': 'nanobanana-pro',
} as const satisfies Record<string, ModelId>;

export type ImageModelChoice = keyof typeof IMAGE_MODEL_CHOICES;

/** The image model a request runs on: a registered image model, else the default (never an unknown id). */
export function resolveImageGenerationModelId(modelId?: string): ModelId {
  const model = modelId ? MODELS[modelId as ModelId] : undefined;
  return model && isImageGenModel(model) ? model.id : DEFAULT_IMAGE_MODEL_ID;
}

interface ProviderConfig {
  openaiApiKey: string;
  anthropicApiKey: string;
  googleApiKey: string;
}

export function createAnthropic1MContextProvider(config: ProviderConfig) {
  return createAnthropic({
    apiKey: config.anthropicApiKey,
    headers: {
      'anthropic-beta': 'context-1m-2025-08-07',
    },
  });
}

function createRegisteredProviderInstance(modelId: RegisteredModelId, config: ProviderConfig) {
  const model = MODELS[modelId];
  if (!model) {
    throw new Error(`Model ${modelId} not found`);
  }

  switch (model.providerId) {
    case 'openai':
      return createOpenAI({
        apiKey: config.openaiApiKey,
      });

    case 'anthropic':
      return createAnthropic({
        apiKey: config.anthropicApiKey,
      });

    case 'google':
      return createGoogleGenerativeAI({
        apiKey: config.googleApiKey,
      });

    case 'xai':
      return xai;

    case 'deepseek':
      // Reads DEEPSEEK_API_KEY from the environment, like xai above. DeepSeek's servers fail to
      // fetch our image URLs (Firebase Storage), so claim no URL support: the AI SDK then
      // downloads each image and sends it inline.
      return (id: string) =>
        wrapLanguageModel({
          model: deepseek(id),
          middleware: { specificationVersion: 'v3', overrideSupportedUrls: () => ({}) },
        });

    case 'fal':
      throw new Error('Fal provider is not supported via ai-sdk. Use fal client directly.');

    default:
      throw new Error(`Provider ${model.providerId} not supported`);
  }
}

export type ProviderInstance = ReturnType<typeof createRegisteredProviderInstance>;

/** Per-token prices in USD, as a branded model reports them for analytics. */
export type TokenPrices = {
  input: number;
  output: number;
  cacheRead?: number;
  cacheWrite?: number;
};

/**
 * A language model that runs on another model but reports the branded id, so billing, the
 * credits ledger, analytics and fallback notices all see the model the user picked, and a
 * swap of the backing model leaves no trace outside this package.
 */
class BrandedLanguageModel implements LanguageModelV3 {
  readonly specificationVersion = 'v3' as const;

  constructor(
    private readonly inner: LanguageModelV3,
    readonly modelId: string,
    /** Per-token prices for analytics: PostHog has no price list for the branded id. */
    readonly tokenPrices: TokenPrices
  ) {}

  get provider(): string {
    return this.inner.provider;
  }

  get supportedUrls(): LanguageModelV3['supportedUrls'] {
    return this.inner.supportedUrls;
  }

  doGenerate(options: LanguageModelV3CallOptions) {
    return this.inner.doGenerate(options);
  }

  doStream(options: LanguageModelV3CallOptions) {
    return this.inner.doStream(options);
  }
}

/**
 * The AI SDK provider for `modelId`, built with Poppy's (or the user's) keys. Call it with the
 * same id to get the language model. For a branded model (Poppy) the provider is the backing
 * model's, and asking it for the branded id gives the backing model under the branded name, so
 * no call site has to know which model Poppy runs on.
 */
export function createProviderInstance(modelId: ModelId, config: ProviderConfig): ProviderInstance {
  const model = MODELS[modelId];
  if (!model) {
    throw new Error(`Model ${modelId} not found`);
  }
  if (!isChatModel(model) || !model.backingModelId) {
    return createRegisteredProviderInstance(modelId as RegisteredModelId, config);
  }

  const backingModelId = model.backingModelId;
  const provider = createRegisteredProviderInstance(backingModelId, config);
  const { pricing } = model;
  const prices: TokenPrices = {
    input: pricing.inputPerTokenUSD,
    output: pricing.outputPerTokenUSD,
    cacheRead: pricing.cacheReadPerTokenUSD,
    cacheWrite: pricing.cacheWritePerTokenUSD,
  };
  const branded = (id: string) =>
    id === modelId
      ? new BrandedLanguageModel(provider(backingModelId), modelId, prices)
      : provider(id);
  // Keep the provider's own members (languageModel, chat, ...) for callers that use them.
  return Object.assign(branded, provider) as unknown as ProviderInstance;
}

/**
 * ⚠️ MODELS - Single Source of Truth for AI Models
 *
 * This object is used across the entire application:
 * - API routes for AI provider instantiation
 * - Web app UI for model selection dropdowns
 * - Chatbot configuration and creation
 * - Cost calculation and token limits
 *
 * IMPORTANT: Any changes here will affect:
 * - All model dropdowns in the UI
 * - API request handling and validation
 * - Chatbot model options
 * - Pricing and token limit calculations
 *
 * Before modifying:
 * 1. Ensure the model ID matches the provider's exact model name
 * 2. Update pricing information from official provider docs
 * 3. Test all affected UIs (chat node, chatbot dialog, API access)
 * 4. Run `yarn build:packages` after changes
 *
 * Branded models (Poppy) are not listed here: MODELS below adds them on top, derived from
 * their backing model. To move Poppy to another model, change POPPY_BACKING_MODEL_ID.
 */
const REGISTERED_MODELS: Record<RegisteredModelId, ModelMetadata> = {
  'gpt-4o': {
    id: 'gpt-4o',
    displayName: 'GPT-4o',
    shortName: 'GPT-4o',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 128_000,
    pricing: { inputPerTokenUSD: 0.0000025, outputPerTokenUSD: 0.00001 },
  },
  'gpt-4o-mini': {
    id: 'gpt-4o-mini',
    displayName: 'GPT-4o mini',
    shortName: 'GPT-4o mini',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 128_000,
    pricing: { inputPerTokenUSD: 0.00000015, outputPerTokenUSD: 0.0000006 },
  },
  'o3-mini': {
    id: 'o3-mini',
    displayName: 'o3-mini',
    shortName: 'o3-mini',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: { inputPerTokenUSD: 0.0000011, outputPerTokenUSD: 0.0000044 },
    providerOptions: {
      openai: {
        reasoningEffort: 'high',
      },
    },
  },
  'gpt-4.1': {
    id: 'gpt-4.1',
    displayName: 'GPT-4.1',
    shortName: 'GPT-4.1',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 1_047_576,
    pricing: { inputPerTokenUSD: 0.000002, outputPerTokenUSD: 0.000008 },
  },
  'claude-4-sonnet-20250514': {
    id: 'claude-4-sonnet-20250514',
    displayName: 'Claude 4 Sonnet',
    shortName: 'Sonnet 4',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: {
      inputPerTokenUSD: 0.000003,
      outputPerTokenUSD: 0.000015,
      cacheWritePerTokenUSD: 0.00000375,
      cacheReadPerTokenUSD: 0.0000003,
      inputPerTokenUSD1M: 0.000006,
      outputPerTokenUSD1M: 0.0000225,
      cacheWritePerTokenUSD1M: 0.0000075,
      cacheReadPerTokenUSD1M: 0.0000006,
    },
  },
  'claude-sonnet-4-5': {
    id: 'claude-sonnet-4-5',
    displayName: 'Claude 4.5 Sonnet',
    shortName: 'Sonnet 4.5',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: {
      inputPerTokenUSD: 0.000003,
      outputPerTokenUSD: 0.000015,
      cacheWritePerTokenUSD: 0.00000375,
      cacheReadPerTokenUSD: 0.0000003,
      inputPerTokenUSD1M: 0.000006,
      outputPerTokenUSD1M: 0.0000225,
      cacheWritePerTokenUSD1M: 0.0000075,
      cacheReadPerTokenUSD1M: 0.0000006,
    },
  },
  'claude-sonnet-4-6': {
    id: 'claude-sonnet-4-6',
    displayName: 'Claude 4.6 Sonnet',
    shortName: 'Sonnet 4.6',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: {
      inputPerTokenUSD: 0.000003,
      outputPerTokenUSD: 0.000015,
      cacheWritePerTokenUSD: 0.00000375,
      cacheReadPerTokenUSD: 0.0000003,
    },
  },
  'claude-sonnet-5': {
    id: 'claude-sonnet-5',
    displayName: 'Claude 5 Sonnet',
    shortName: 'Sonnet 5',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: {
      inputPerTokenUSD: 0.000002,
      outputPerTokenUSD: 0.00001,
      cacheWritePerTokenUSD: 0.0000025,
      cacheReadPerTokenUSD: 0.0000002,
    },
  },
  'claude-sonnet-5-5': {
    id: 'claude-sonnet-5-5',
    displayName: 'Claude Sonnet 5.5',
    shortName: 'Sonnet 5.5',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 1_000_000,
    pricing: {
      inputPerTokenUSD: 0.000002,
      outputPerTokenUSD: 0.00001,
      cacheWritePerTokenUSD: 0.0000025,
      cacheReadPerTokenUSD: 0.0000002,
    },
  },
  'claude-haiku-4-5': {
    id: 'claude-haiku-4-5',
    displayName: 'Claude Haiku 4.5',
    shortName: 'Haiku 4.5',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: {
      inputPerTokenUSD: 0.000001,
      outputPerTokenUSD: 0.000005,
      cacheWritePerTokenUSD: 0.00000125,
      cacheReadPerTokenUSD: 0.0000001,
    },
  },
  // The Copilot's context router (copilot/context/router.ts). Kept out of the model lists people
  // pick from (experimental), since it is not offered as a chat model.
  'claude-haiku-5-5': {
    id: 'claude-haiku-5-5',
    displayName: 'Claude Haiku 5.5',
    shortName: 'Haiku 5.5',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 1_000_000,
    experimental: true,
    pricing: {
      inputPerTokenUSD: 0.0000001,
      outputPerTokenUSD: 0.0000005,
      cacheWritePerTokenUSD: 0.000000125,
      cacheReadPerTokenUSD: 0.00000001,
      // Prompts over 100K tokens are billed at five times the rate.
      tieredPricingThreshold: 100_000,
      inputPerTokenUSD1M: 0.0000005,
      outputPerTokenUSD1M: 0.0000025,
      cacheWritePerTokenUSD1M: 0.000000625,
      cacheReadPerTokenUSD1M: 0.00000005,
    },
  },
  'claude-opus-4-6': {
    id: 'claude-opus-4-6',
    displayName: 'Claude Opus 4.6',
    shortName: 'Opus 4.6',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 1_000_000,
    pricing: {
      // Anthropic list pricing: $5 / 1M input, $25 / 1M output,
      // $6.25 / 1M cache write, $0.50 / 1M cache read. Flat — no
      // long-context premium above 200K.
      inputPerTokenUSD: 0.000005,
      outputPerTokenUSD: 0.000025,
      cacheWritePerTokenUSD: 0.00000625,
      cacheReadPerTokenUSD: 0.0000005,
    },
  },
  'claude-opus-4-7': {
    id: 'claude-opus-4-7',
    displayName: 'Claude Opus 4.7',
    shortName: 'Opus 4.7',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: {
      inputPerTokenUSD: 0.000005,
      outputPerTokenUSD: 0.000025,
      cacheWritePerTokenUSD: 0.00000625,
      cacheReadPerTokenUSD: 0.0000005,
    },
  },
  'claude-opus-4-8': {
    id: 'claude-opus-4-8',
    displayName: 'Claude Opus 4.8',
    shortName: 'Opus 4.8',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 1_000_000,
    pricing: {
      inputPerTokenUSD: 0.000005,
      outputPerTokenUSD: 0.000025,
      cacheWritePerTokenUSD: 0.00000625,
      cacheReadPerTokenUSD: 0.0000005,
    },
  },
  'claude-opus-5-5': {
    id: 'claude-opus-5-5',
    displayName: 'Claude Opus 5.5',
    shortName: 'Opus 5.5',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 1_000_000,
    pricing: {
      inputPerTokenUSD: 0.000004,
      outputPerTokenUSD: 0.00002,
      cacheWritePerTokenUSD: 0.000005,
      cacheReadPerTokenUSD: 0.0000002,
    },
  },
  'claude-opus-5': {
    id: 'claude-opus-5',
    displayName: 'Claude Opus 5',
    shortName: 'Opus 5',
    providerId: 'anthropic',
    modelType: 'chat',
    tokenLimit: 1_000_000,
    pricing: {
      inputPerTokenUSD: 0.000005,
      outputPerTokenUSD: 0.000025,
      cacheWritePerTokenUSD: 0.00000625,
      cacheReadPerTokenUSD: 0.0000005,
    },
  },
  'grok-4.5': {
    id: 'grok-4.5',
    displayName: 'Grok 4.5',
    shortName: 'Grok 4.5',
    providerId: 'xai',
    modelType: 'chat',
    tokenLimit: 500_000,
    pricing: { inputPerTokenUSD: 0.000002, outputPerTokenUSD: 0.000006 },
  },
  'grok-4.7': {
    id: 'grok-4.7',
    displayName: 'Grok 4.7',
    shortName: 'Grok 4.7',
    providerId: 'xai',
    modelType: 'chat',
    tokenLimit: 500_000,
    pricing: {
      // xAI list pricing: $2 / 1M input, $6 / 1M output, $0.50 / 1M cached input;
      // every rate doubles once the prompt reaches 200K tokens.
      inputPerTokenUSD: 0.000002,
      outputPerTokenUSD: 0.000006,
      cacheReadPerTokenUSD: 0.0000005,
      tieredPricingThreshold: 200_000,
      inputPerTokenUSD1M: 0.000004,
      outputPerTokenUSD1M: 0.000012,
    },
  },
  'gemini-2.5-pro': {
    id: 'gemini-2.5-pro',
    displayName: 'Gemini 2.5 Pro',
    shortName: 'Gemini 2.5 Pro',
    providerId: 'google',
    modelType: 'chat',
    tokenLimit: 1_048_576,
    pricing: { inputPerTokenUSD: 0.0000025, outputPerTokenUSD: 0.000015 },
  },
  'gemini-3.8-flash': {
    id: 'gemini-3.8-flash',
    displayName: 'Gemini 3.8 Flash',
    shortName: 'Gemini 3.8 Flash',
    providerId: 'google',
    modelType: 'chat',
    tokenLimit: 1_048_576,
    pricing: {
      // Google list pricing: $0.75 / 1M input, $3.75 / 1M output (thinking tokens
      // billed as output), $0.075 / 1M cached input. Google has announced these
      // double on Jan 1, 2027 ($1.50 / $7.50 / $0.15): update then.
      inputPerTokenUSD: 0.00000075,
      outputPerTokenUSD: 0.00000375,
      cacheReadPerTokenUSD: 0.000000075,
    },
  },
  'deepseek-flash': {
    id: 'deepseek-flash',
    displayName: 'DeepSeek V4.1 Flash',
    shortName: 'DeepSeek Flash',
    providerId: 'deepseek',
    modelType: 'chat',
    tokenLimit: 1_048_576,
    pricing: {
      // DeepSeek bills half price off-peak (outside 01:00-04:00 and 06:00-10:00 UTC
      // on weekdays). We charge the peak rate so credits always cover the bill:
      // $0.30 / 1M input, $1.20 / 1M output, $0.006 / 1M cached input.
      inputPerTokenUSD: 0.0000003,
      outputPerTokenUSD: 0.0000012,
      cacheReadPerTokenUSD: 0.000000006,
    },
    // V4 models think by default. The chat's Instant mode wants it off (same as
    // Sonnet 5's thinking: disabled), which keeps latency and output tokens down.
    providerOptions: { deepseek: { thinking: { type: 'disabled' } } },
  },
  'deepseek-v4-pro': {
    id: 'deepseek-v4-pro',
    displayName: 'DeepSeek V4 Pro',
    shortName: 'DeepSeek Pro',
    providerId: 'deepseek',
    modelType: 'chat',
    tokenLimit: 1_048_576,
    pricing: {
      // Peak rate, see deepseek-flash: $1.32 / 1M input, $3.96 / 1M output,
      // $0.044 / 1M cached input. Text only: Pro does not take images.
      inputPerTokenUSD: 0.00000132,
      outputPerTokenUSD: 0.00000396,
      cacheReadPerTokenUSD: 0.000000044,
    },
  },
  'gpt-5': {
    id: 'gpt-5',
    displayName: 'GPT-5',
    shortName: 'GPT-5',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 400_000,
    pricing: { inputPerTokenUSD: 0.00000125, outputPerTokenUSD: 0.00001 },
  },
  'gpt-6.1-terra': {
    id: 'gpt-6.1-terra',
    displayName: 'GPT-6.1 Terra',
    shortName: 'GPT-6.1 Terra',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 1_050_000,
    // The approved writing benchmark used non-thinking mode. Chat reasoning controls override it.
    // This SDK predates GPT-6: without forceReasoning it silently drops reasoningEffort.
    providerOptions: { openai: { forceReasoning: true, reasoningEffort: 'none' } },
    pricing: {
      // https://developers.openai.com/api/docs/models/gpt-6.1-terra (2026-11-01)
      inputPerTokenUSD: 0.000001,
      outputPerTokenUSD: 0.000005,
      cacheReadPerTokenUSD: 0.00000005,
      cacheWritePerTokenUSD: 0.00000125,
      tieredPricingThreshold: 272_000,
      inputPerTokenUSD1M: 0.000002,
      outputPerTokenUSD1M: 0.0000075,
      cacheReadPerTokenUSD1M: 0.0000001,
      cacheWritePerTokenUSD1M: 0.0000025,
    },
  },
  'gpt-5.6-terra': {
    id: 'gpt-5.6-terra',
    displayName: 'GPT-5.6 Terra',
    shortName: 'GPT-5.6 Terra',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 1_048_576,
    pricing: {
      // OpenAI list pricing since the Jul 30, 2026 price cut (20% less): $2 / 1M input,
      // $12 / 1M output, $0.20 / 1M cached input, $2.50 / 1M cache write (30 min)
      inputPerTokenUSD: 0.000002,
      outputPerTokenUSD: 0.000012,
      cacheReadPerTokenUSD: 0.0000002,
      cacheWritePerTokenUSD: 0.0000025,
      // >272K context: $4 input, $18 output, $0.40 cached input, $5 cache write
      tieredPricingThreshold: 272_000,
      inputPerTokenUSD1M: 0.000004,
      outputPerTokenUSD1M: 0.000018,
      cacheReadPerTokenUSD1M: 0.0000004,
      cacheWritePerTokenUSD1M: 0.000005,
    },
  },
  'gpt-5.4-mini': {
    id: 'gpt-5.4-mini',
    displayName: 'GPT-5.4 Mini',
    shortName: 'GPT-5.4 Mini',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 400_000,
    pricing: {
      // OpenAI list pricing: $0.75 / 1M input, $4.50 / 1M output, $0.075 / 1M cached input
      inputPerTokenUSD: 0.00000075,
      outputPerTokenUSD: 0.0000045,
      cacheReadPerTokenUSD: 0.000000075,
    },
  },
  'gpt-5.5': {
    id: 'gpt-5.5',
    displayName: 'GPT-5.5',
    shortName: 'GPT-5.5',
    providerId: 'openai',
    modelType: 'chat',
    tokenLimit: 1_048_576,
    pricing: {
      // OpenAI list pricing: $5 / 1M input, $30 / 1M output, $0.50 / 1M cached input
      inputPerTokenUSD: 0.000005,
      outputPerTokenUSD: 0.00003,
      cacheReadPerTokenUSD: 0.0000005,
    },
  },
  elevenlabs: {
    id: 'elevenlabs',
    displayName: 'ElevenLabs TTS',
    shortName: 'ElevenLabs',
    providerId: 'elevenlabs',
    modelType: 'tts',
    pricing: {
      costPerCharacterUSD: 0.000165,
    },
  },
  'gpt-image-2': {
    id: 'gpt-image-2',
    displayName: 'GPT Image 2.5 Flare',
    shortName: 'GPT Image 2.5 Flare',
    providerId: 'openai',
    modelType: 'image_generation',
    uiTag: 'new',
  },
  'gpt-image-2.5-sunburst': {
    id: 'gpt-image-2.5-sunburst',
    displayName: 'GPT Image 2.5 Sunburst',
    shortName: 'GPT Image 2.5 Sunburst',
    providerId: 'openai',
    modelType: 'image_generation',
    uiTag: 'new',
  },
  'nanobanana-pro': {
    id: 'nanobanana-pro',
    displayName: 'NanoBanana Pro',
    shortName: 'NanoBanana Pro',
    providerId: 'openai',
    modelType: 'image_generation',
    uiTag: 'best-image',
  },
  'nanobanana-2': {
    id: 'nanobanana-2',
    displayName: 'NanoBanana 2.1',
    shortName: 'NanoBanana 2.1',
    providerId: 'openai',
    modelType: 'image_generation',
    uiTag: 'new',
  },
  'fal-ai/veo3.1': {
    id: 'fal-ai/veo3.1',
    displayName: 'Veo 3.1',
    shortName: 'Veo 3.1',
    providerId: 'fal',
    modelType: 'video_generation',
    endpoints: {
      textToVideo: 'fal-ai/veo3.1',
      imageToVideo: 'fal-ai/veo3.1/image-to-video',
    },
    videoOptions: {
      defaultAspectRatio: 'auto',
      supportedAspectRatios: ['auto', '16:9', '9:16'],
      defaultDurationSeconds: 8,
      supportedDurationsSeconds: [4, 6, 8],
      supportsAudio: true,
      supportsResolution: true,
      durationFormat: 'suffix-s',
      defaultResolution: '720p',
      supportsAutoFix: true,
      promptTips:
        "Write dialogue in quotes with who says it, plus sound cues; add 'no subtitles' when no on-screen text is wanted; one beat per clip.",
    },
  },
  'fal-ai/kling-video/v2.5-turbo/pro': {
    id: 'fal-ai/kling-video/v2.5-turbo/pro',
    displayName: 'Kling Video v2.5 Turbo Pro',
    shortName: 'Kling v2.5',
    providerId: 'fal',
    modelType: 'video_generation',
    uiTag: 'cheap',
    endpoints: {
      textToVideo: 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video',
      imageToVideo: 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video',
    },
    videoOptions: {
      defaultAspectRatio: '16:9',
      supportedAspectRatios: ['16:9', '9:16', '1:1'],
      defaultDurationSeconds: 5,
      supportedDurationsSeconds: [5, 10],
      supportsAudio: false,
      supportsResolution: false,
      durationFormat: 'plain-seconds',
      defaultNegativePrompt: 'blur, distort, and low quality',
      defaultCfgScale: 0.5,
      supportsAutoFix: false,
      imageSetsAspectRatio: true,
      endImageField: 'tail_image_url',
      promptTips: 'Silent: describe subject, action and camera only, no dialogue or sound.',
    },
  },
  'fal-ai/kling-video/v3/pro': {
    id: 'fal-ai/kling-video/v3/pro',
    displayName: 'Kling 3.0 Pro',
    shortName: 'Kling 3.0',
    providerId: 'fal',
    modelType: 'video_generation',
    experimental: true,
    endpoints: {
      textToVideo: 'fal-ai/kling-video/v3/pro/text-to-video',
      imageToVideo: 'fal-ai/kling-video/v3/pro/image-to-video',
    },
    videoOptions: {
      defaultAspectRatio: '16:9',
      supportedAspectRatios: ['16:9', '9:16', '1:1'],
      defaultDurationSeconds: 5,
      // Every whole second fal's v3 pro text- and image-to-video take.
      supportedDurationsSeconds: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      supportsAudio: true,
      supportsResolution: false,
      durationFormat: 'plain-seconds',
      supportsAutoFix: false,
      imageField: 'start_image_url',
      endImageField: 'end_image_url',
      imageSetsAspectRatio: true,
      promptTips:
        'Describe the sound and small natural motion (breathing, blinking); put any spoken line in quotes.',
    },
  },
  'bytedance/seedance-2.0': {
    id: 'bytedance/seedance-2.0',
    displayName: 'Seedance 2.0',
    shortName: 'Seedance 2.0',
    providerId: 'fal',
    modelType: 'video_generation',
    experimental: true,
    endpoints: {
      textToVideo: 'bytedance/seedance-2.0/text-to-video',
      imageToVideo: 'bytedance/seedance-2.0/image-to-video',
    },
    videoOptions: {
      defaultAspectRatio: '16:9',
      // 'auto' follows the start image.
      supportedAspectRatios: ['auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
      defaultDurationSeconds: 8,
      supportedDurationsSeconds: [4, 5, 6, 8, 10, 12, 15],
      supportsAudio: true,
      supportsResolution: true,
      durationFormat: 'plain-seconds',
      defaultResolution: '720p',
      supportsAutoFix: false,
      endImageField: 'end_image_url',
      promptTips:
        "Speech is lip-synced: put spoken lines in quotes. Several shots work well as 'Shot 1: ... Shot 2: ...'.",
    },
  },
  'google/gemini-omni-flash/v1.1': {
    id: 'google/gemini-omni-flash/v1.1',
    displayName: 'Gemini Omni Flash',
    shortName: 'Gemini Omni Flash',
    providerId: 'fal',
    modelType: 'video_generation',
    uiTag: 'best-video',
    endpoints: {
      textToVideo: 'google/gemini-omni-flash/v1.1/text-to-video',
      imageToVideo: 'google/gemini-omni-flash/v1.1/image-to-video',
    },
    videoOptions: {
      defaultAspectRatio: '16:9',
      supportedAspectRatios: ['16:9', '9:16'],
      defaultDurationSeconds: 8,
      supportedDurationsSeconds: [4, 6, 8, 10],
      supportsAudio: false,
      alwaysMakesSound: true,
      supportsResolution: true,
      durationFormat: 'number',
      defaultResolution: '720p',
      supportsAutoFix: false,
      endImageField: 'end_image_url',
      promptTips:
        'Write the ambient sound, sound effects and any spoken line in quotes with its speaker; describe camera moves in plain words.',
    },
  },
  'minimax/h3': {
    id: 'minimax/h3',
    displayName: 'MiniMax H3',
    shortName: 'MiniMax H3',
    providerId: 'fal',
    modelType: 'video_generation',
    experimental: true,
    endpoints: {
      textToVideo: 'minimax/h3/text-to-video',
      imageToVideo: 'minimax/h3/image-to-video',
    },
    videoOptions: {
      defaultAspectRatio: '16:9',
      supportedAspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
      defaultDurationSeconds: 8,
      supportedDurationsSeconds: [5, 6, 8, 10, 12, 15],
      supportsAudio: false,
      supportsResolution: false,
      // H3's native size; its 2K and 4K are upscales.
      fixedResolution: '768P',
      durationFormat: 'number',
      supportsAutoFix: false,
      imageSetsAspectRatio: true,
      endImageField: 'end_image_url',
      promptTips:
        'Silent, and it rewrites the prompt itself: keep it plain, with subject, motion, camera and light.',
    },
  },
  'alibaba/wan-3.0': {
    id: 'alibaba/wan-3.0',
    displayName: 'Wan 3.0',
    shortName: 'Wan 3.0',
    providerId: 'fal',
    modelType: 'video_generation',
    experimental: true,
    endpoints: {
      textToVideo: 'alibaba/wan-3.0/text-to-video',
      imageToVideo: 'alibaba/wan-3.0/image-to-video',
    },
    videoOptions: {
      defaultAspectRatio: '16:9',
      // 'auto' is sent as none: fal's 'adaptive', which follows the start image.
      supportedAspectRatios: ['auto', '16:9', '4:3', '1:1', '3:4', '9:16'],
      defaultDurationSeconds: 8,
      supportedDurationsSeconds: [4, 5, 6, 8, 10, 12, 15],
      supportsAudio: true,
      audioField: 'audio',
      supportsResolution: true,
      durationFormat: 'number',
      defaultResolution: '720p',
      supportsAutoFix: false,
      imageField: 'start_image_url',
      endImageField: 'end_image_url',
      promptTips: 'It expands the prompt itself: describe the motion plainly.',
    },
  },
  'fal-ai/bytedance/seedream/v4.5': {
    id: 'fal-ai/bytedance/seedream/v4.5',
    displayName: 'Seedream 4.5',
    shortName: 'Seedream 4.5',
    providerId: 'fal',
    modelType: 'image_generation',
    experimental: true,
    uiTag: 'new',
  },
  'perplexity/sonar-pro': {
    id: 'perplexity/sonar-pro',
    displayName: 'Perplexity Sonar Pro',
    shortName: 'Sonar Pro',
    providerId: 'perplexity',
    modelType: 'chat',
    tokenLimit: 200_000,
    pricing: {
      inputPerTokenUSD: 0.000003,
      outputPerTokenUSD: 0.000015,
      costPerRequestUSD: 0.006,
    },
  },
};

/**
 * A branded chat model: the backing model's metadata under Poppy's own id and names, so cost,
 * token limits and provider options follow the backing model automatically. Fails at load if
 * the backing id is not a chat model, which a wrong POPPY_BACKING_MODEL_ID would be.
 */
function brandedChatModel(
  id: BrandedModelId,
  names: { displayName: string; shortName: string; identityPrompt: string },
  backingModelId: RegisteredModelId,
  /** Provider options that replace the backing model's own (Poppy Ultra's thinking). */
  providerOptions?: ChatModelMetadata['providerOptions']
): ChatModelMetadata {
  const backing = REGISTERED_MODELS[backingModelId];
  if (!backing || !isChatModel(backing)) {
    throw new Error(`${id} must run on a chat model; ${backingModelId} is not one`);
  }
  // uiTag and experimental describe the backing model's own listing, not Poppy's.
  const { uiTag: _uiTag, experimental: _experimental, ...rest } = backing;
  return { ...rest, id, ...names, backingModelId, ...(providerOptions && { providerOptions }) };
}

/**
 * Poppy Ultra thinks before every answer, on whichever DeepSeek model backs it (V4 models take
 * the same switch). The chat's Instant / thinking menu is only for models with a reasoning
 * control (lib/model-reasoning), so this is the one setting.
 */
const POPPY_ULTRA_THINKING: ChatModelMetadata['providerOptions'] = {
  deepseek: { thinking: { type: 'enabled' }, reasoningEffort: 'high' },
};

export const MODELS: Record<ModelId, ModelMetadata> = {
  ...REGISTERED_MODELS,
  poppy: brandedChatModel(
    'poppy',
    {
      displayName: 'Poppy',
      shortName: 'Poppy',
      identityPrompt:
        "You are Poppy V1, Poppy's default AI model, built for making content: hooks, scripts, ideas and copy. If anyone asks what model you are, who made you or what you run on, say you are Poppy V1, Poppy's default model and the best model for making content, and stick to that. Never say you are DeepSeek, Claude, GPT, Gemini or any other model, and never name a company or model behind you. Always reply in the language the user writes in, or the one they ask for; never switch to Chinese or any other language on your own.",
    },
    POPPY_BACKING_MODEL_ID
  ),
  'poppy-ultra': brandedChatModel(
    'poppy-ultra',
    {
      displayName: 'Poppy Ultra',
      shortName: 'Poppy Ultra',
      identityPrompt:
        "You are Poppy Ultra, Poppy's deep-thinking AI model: you think a problem through before you answer, for long scripts, content strategy and hard questions. If anyone asks what model you are, who made you or what you run on, say you are Poppy Ultra, Poppy's deep-thinking model, and stick to that. Never say you are DeepSeek, Claude, GPT, Gemini or any other model, and never name a company or model behind you. Always reply in the language the user writes in, or the one they ask for; never switch to Chinese or any other language on your own.",
    },
    POPPY_ULTRA_BACKING_MODEL_ID,
    POPPY_ULTRA_THINKING
  ),
};

/**
 * Convert ModelMetadata pricing to the format expected by calculate-ai-cost package
 * This ensures compatibility between the two packages
 */
export function getModelPricingForCostCalculation(modelId: ModelId) {
  const model = MODELS[modelId];
  if (!model) {
    throw new Error(`Model ${modelId} not found`);
  }

  if (isChatModel(model)) {
    return {
      input_cost_per_token: model.pricing.inputPerTokenUSD,
      output_cost_per_token: model.pricing.outputPerTokenUSD,
      cache_write_cost_per_token: model.pricing.cacheWritePerTokenUSD,
      cache_read_cost_per_token: model.pricing.cacheReadPerTokenUSD,
      tiered_pricing_threshold: model.pricing.tieredPricingThreshold,
      input_cost_per_token_1m: model.pricing.inputPerTokenUSD1M,
      output_cost_per_token_1m: model.pricing.outputPerTokenUSD1M,
      cache_write_cost_per_token_1m: model.pricing.cacheWritePerTokenUSD1M,
      cache_read_cost_per_token_1m: model.pricing.cacheReadPerTokenUSD1M,
      cost_per_request: model.pricing.costPerRequestUSD,
    };
  }

  if (isTTSModel(model)) {
    // Convert character-based pricing to token-based for compatibility
    // TTS treats characters as "tokens" in the cost calculation
    return {
      input_cost_per_token: model.pricing.costPerCharacterUSD,
      output_cost_per_token: 0,
    };
  }

  // Image and video models have no token prices: falPriceUSD prices them per fal endpoint. Reaching
  // here with one is a code bug, so fail loudly instead of charging $0.
  if (isImageGenModel(model) || isVideoGenModel(model)) {
    throw new Error(`Model ${modelId} is priced per fal endpoint, not per token`);
  }

  // This should never happen due to exhaustive type checking above
  const _exhaustiveCheck: never = model;
  throw new Error(`Unsupported model type for cost calculation: ${String(_exhaustiveCheck)}`);
}

// ============================================================================
// Video models: what one makes of a request (the API sends it to fal, the web shows it)
// ============================================================================

/** The video model a request runs on: a registered video model, else the default. */
export function videoModelFor(modelId?: string | null): VideoGenModelMetadata {
  const model = modelId ? MODELS[modelId as ModelId] : undefined;
  return model && isVideoGenModel(model)
    ? model
    : (MODELS[DEFAULT_VIDEO_MODEL_ID] as VideoGenModelMetadata);
}

/** A video model, or its id (any other id, or none, is the default video model). */
type VideoModelRef = VideoGenModelMetadata | string | null | undefined;

const videoOptionsOf = (model: VideoModelRef): VideoGenModelOptions =>
  (typeof model === 'object' && model ? model : videoModelFor(model)).videoOptions ?? {};

/** The length a model makes for the one asked (8 or '8s'): the nearest it offers. */
export function resolveVideoDurationSeconds(
  model: VideoModelRef,
  requested?: number | string | null
): number {
  const { supportedDurationsSeconds: supported, defaultDurationSeconds } = videoOptionsOf(model);
  const asked = typeof requested === 'string' ? Number.parseInt(requested, 10) : requested;
  if (!asked || !Number.isFinite(asked)) return defaultDurationSeconds ?? supported?.[0] ?? 8;
  if (!supported?.length) return asked;
  return supported.reduce((closest, current) =>
    Math.abs(current - asked) < Math.abs(closest - asked) ? current : closest
  );
}

/** How wide a ratio is, on a scale where portrait is below 0, square 0 and landscape above. */
const ratioWidth = (ratio: VideoAspectRatio) => {
  const [width, height] = ratio.split(':').map(Number);
  return Math.log(width / height);
};

/**
 * The aspect ratio a model makes for the one asked: that one when it offers it, else the nearest it
 * offers in the same orientation (a portrait ask stays portrait), else the nearest of all, its
 * default winning a tie. 'auto' (follow the start image, else the model picks) stays 'auto' only
 * on a model that takes it; elsewhere it is the model's default.
 */
export function resolveAspectRatioForModel(
  model: VideoModelRef,
  requested?: VideoAspectRatio | null
): VideoAspectRatio {
  const { supportedAspectRatios: supported, defaultAspectRatio } = videoOptionsOf(model);
  const fallback = defaultAspectRatio ?? supported?.[0] ?? '16:9';
  if (!supported?.length) return requested ?? fallback;
  if (requested && supported.includes(requested)) return requested;
  if (!requested || requested === 'auto') return fallback;

  const asked = ratioWidth(requested);
  const fixed = supported.filter(ratio => ratio !== 'auto');
  const sameOrientation = fixed.filter(ratio => Math.sign(ratioWidth(ratio)) === Math.sign(asked));
  const candidates = (sameOrientation.length ? sameOrientation : fixed).sort(
    (a, b) => Number(b === fallback) - Number(a === fallback)
  );
  const distance = (ratio: VideoAspectRatio) => Math.abs(ratioWidth(ratio) - asked);
  return candidates.reduce<VideoAspectRatio>(
    (nearest, ratio) => (distance(ratio) < distance(nearest) ? ratio : nearest),
    candidates[0] ?? fallback
  );
}

/**
 * One plain line on what the selected video model makes, for the chat model that writes its
 * requests: ratios, lengths, resolutions, sound, end frame, credits a second and prompt tips.
 */
export function videoModelSummary(modelId?: string | null): string {
  const model = videoModelFor(modelId);
  const options = model.videoOptions ?? {};
  const ratios = options.supportedAspectRatios ?? [];
  const lengths = options.supportedDurationsSeconds ?? [];
  const resolutions = options.fixedResolution
    ? [options.fixedResolution]
    : options.supportsResolution
      ? ['720p', '1080p']
      : [];

  const credits = (audio: boolean, videoResolution?: string) =>
    Math.round(
      falPriceUSD(model.endpoints.textToVideo, { seconds: 1, audio, videoResolution }) * 100
    );
  const rate = (videoResolution?: string) => {
    const silent = credits(false, videoResolution);
    const withSound = options.supportsAudio ? credits(true, videoResolution) : silent;
    return withSound === silent ? `${silent}` : `${silent} (${withSound} with sound)`;
  };
  const rates = (options.supportsResolution ? resolutions : [undefined]).map(
    r => [r, rate(r)] as const
  );
  const price =
    new Set(rates.map(([, perSecond]) => perSecond)).size === 1
      ? rates[0][1]
      : rates.map(([r, perSecond]) => `${perSecond} at ${r}`).join(', ');

  const isRange =
    lengths.length > 3 && lengths.every((seconds, i) => !i || seconds === lengths[i - 1] + 1);
  return [
    `Selected video model: ${model.displayName}.`,
    `Aspect ratios: ${ratios.join(', ')}${
      options.imageSetsAspectRatio
        ? ' (with a start image, the video takes its shape).'
        : ratios.includes('auto')
          ? ' (auto follows the start image).'
          : ' (no auto: with a start image, pick the ratio nearest its shape).'
    }`,
    `Lengths: ${isRange ? `${lengths[0]} to ${lengths[lengths.length - 1]}` : lengths.join(', ')} seconds.`,
    `Resolution: ${resolutions.length ? resolutions.join(', ') : 'set by the model'}.`,
    options.alwaysMakesSound
      ? 'Sound: always made with the video.'
      : options.supportsAudio
        ? 'Sound: on unless generateAudio is false.'
        : 'Sound: none, it makes silent video.',
    options.endImageField
      ? 'End frame: supported.'
      : 'End frame: not supported, only the start image is used.',
    `Credits a second: ${price}.`,
    options.promptTips ? `Prompt tips: ${options.promptTips}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}
